import React, { useState, useEffect } from 'react';
import { StandardTableActions, Badge, statusVariantFromBoolean } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { CatalogItem } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import { EntityImageGallery } from '../../components/EntityImageGallery';
import { API_BASE_URL } from '../../config/apiConfig';
import api from '../../services/api';
import { CatalogItemUploadSection } from './CatalogItemUploadSection';
import { ProductPhotosFormBlock } from './ProductPhotosFormBlock';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';

export const moduleConfig = {
  module: {
    title: 'Catalog',
    description: 'Products and services',
    permissionModuleKey: 'catalog_items',
    route: {
      list: '/catalog-items',
      new: '/catalog-items/new',
      detail: (id: number) => `/catalog-items/${id}`,
      edit: (id: number) => `/catalog-items/${id}/edit`,
    },
    api: {
      list: '/catalog-items',
      detail: (id: number) => `/catalog-items/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search by name...',
  },
  filterGroups: [{ name: 'basic', label: 'Basic', icon: '🔍', fields: ['type', 'name', 'manufacturerId', 'categoryId', 'isActive'] }],
  filters: {
    fields: [
      { key: 'type', label: 'Type', placeholder: 'All types', type: 'select' as const, getValue: (item: CatalogItem) => item.type, options: [{ value: '', label: 'All' }, { value: 'product', label: 'Product' }, { value: 'service', label: 'Service' }] },
      { key: 'name', label: 'Name', placeholder: 'Search name...', type: 'text' as const, getValue: (item: CatalogItem) => item.name ?? '', options: [] },
      { key: 'manufacturerId', label: 'Manufacturer', placeholder: 'All manufacturers', type: 'select' as const, getValue: (item: CatalogItem) => item.manufacturer?.name ?? (item.manufacturerId != null ? `#${item.manufacturerId}` : ''), options: [], apiEndpoint: '/manufacturers', labelField: 'name' },
      { key: 'categoryId', label: 'Category', placeholder: 'All categories', type: 'select' as const, getValue: (item: CatalogItem) => item.category?.name ?? (item.categoryId != null ? `#${item.categoryId}` : ''), options: [], apiEndpoint: '/catalog-categories', labelField: 'name' },
      { key: 'isActive', label: 'Active', placeholder: 'All', type: 'boolean' as const, getValue: (item: CatalogItem) => (item.isActive ? 'Active' : 'Inactive'), options: ['Active', 'Inactive'] },
    ],
    applyFilter: (items: CatalogItem[], filters: Record<string, string[] | string>) => {
      return items.filter((item) => {
        const typeArr = Array.isArray(filters.type) ? filters.type : filters.type ? [filters.type] : [];
        if (typeArr.length > 0 && typeArr.some((v) => (v || '').trim())) {
          const typeMatch = typeArr.some((f) => (item.type || '') === String(f).trim());
          if (!typeMatch) return false;
        }

        const nameFilter = typeof filters.name === 'string' ? filters.name : Array.isArray(filters.name) ? (filters.name as string[])[0] : '';
        if (nameFilter && String(nameFilter).trim()) {
          const name = (item.name || '').toLowerCase();
          if (!name.includes(String(nameFilter).trim().toLowerCase())) return false;
        }

        const manArr = Array.isArray(filters.manufacturerId) ? filters.manufacturerId : filters.manufacturerId ? [filters.manufacturerId] : [];
        if (manArr.length > 0 && manArr.some((v) => (v || '').trim())) {
          const displayName = item.manufacturer?.name ?? (item.manufacturerId != null ? `#${item.manufacturerId}` : '');
          const match = manArr.some((f) => {
            const v = String(f).trim();
            return displayName === v || (item.manufacturerId != null && v === `#${item.manufacturerId}`);
          });
          if (!match) return false;
        }

        const catArr = Array.isArray(filters.categoryId) ? filters.categoryId : filters.categoryId ? [filters.categoryId] : [];
        if (catArr.length > 0 && catArr.some((v) => (v || '').trim())) {
          const displayName = item.category?.name ?? (item.categoryId != null ? `#${item.categoryId}` : '');
          const match = catArr.some((f) => {
            const v = String(f).trim();
            return displayName === v || (item.categoryId != null && v === `#${item.categoryId}`);
          });
          if (!match) return false;
        }

        const activeArr = Array.isArray(filters.isActive) ? filters.isActive : filters.isActive ? [filters.isActive] : [];
        if (activeArr.length > 0 && activeArr.some((v) => (v || '').trim())) {
          const status = item.isActive ? 'Active' : 'Inactive';
          if (!activeArr.includes(status)) return false;
        }

        return true;
      });
    },
  },
  toolbar: {
    actions: [
      { key: 'add', icon: 'add', title: 'Add item', showTitle: false, variant: 'primary' as const, href: '/catalog-items/new' },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: CatalogItem) => { window.location.href = `/catalog-items/${item.id}`; },
    keyExtractor: (item: CatalogItem) => item.id,
  },
  emptyStates: {
    noData: 'Add a product or service to get started.',
    noSearchResults: 'No catalog items match your search.',
    noFilterResults: 'No catalog items match the selected filters.',
  },
  delete: {
    title: 'Delete catalog item',
    message: 'Are you sure you want to delete this item?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete catalog item.',
    handler: async (id: number) => {
      const { deleteCatalogItem } = await import('../../services/mutations');
      await deleteCatalogItem(id);
    },
  },
  utils: {
    formatDate: (dateString?: string | null) => (dateString ? formatDateGlobal(dateString) : '-'),
    formatCurrency: (amount?: number | null) =>
      amount == null ? '-' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount),
    /** Display label for type: product → Product, service → Service */
    formatType: (type?: string | null) => (type === 'service' ? 'Service' : type === 'product' ? 'Product' : type || '-'),
  },
  detail: {
    subtitle: 'Catalog item details',
    notFoundTitle: 'Catalog item not found',
    notFoundMessage: 'This item does not exist.',
    notFoundBackText: 'Back to Catalog',
    defaultTitle: 'Unnamed',
    getTitle: (item: CatalogItem) => item.name || `Item #${item.id}`,
  },
  form: {
    new: { title: 'New catalog item', subtitle: 'Add a product or service', submitText: 'Create' },
    edit: { title: 'Edit catalog item', subtitle: 'Update item', submitText: 'Update' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Failed to save catalog item.',
    /** Redirect to edit after create so user can add product photos right away */
    redirectToEditAfterCreate: true,
    create: async (data: Partial<CatalogItem> & { type?: string; name?: string }) => {
      const { createCatalogItem } = await import('../../services/mutations');
      return createCatalogItem({
        type: data.type ?? 'product',
        name: data.name ?? '',
        description: data.description ?? undefined,
        defaultPrice: data.defaultPrice != null ? Number(data.defaultPrice) : undefined,
        manufacturerId: data.manufacturerId != null && String(data.manufacturerId) !== '' ? Number(data.manufacturerId) : undefined,
        categoryId: data.categoryId != null && String(data.categoryId) !== '' ? Number(data.categoryId) : undefined,
        isActive: data.isActive ?? true,
        sortOrder: data.sortOrder != null ? Number(data.sortOrder) : undefined,
      });
    },
    update: async (id: number, data: Partial<CatalogItem>) => {
      const { updateCatalogItem } = await import('../../services/mutations');
      return updateCatalogItem(id, {
        type: data.type,
        name: data.name,
        description: data.description ?? undefined,
        defaultPrice: data.defaultPrice != null ? Number(data.defaultPrice) : undefined,
        manufacturerId: data.manufacturerId != null && String(data.manufacturerId) !== '' ? Number(data.manufacturerId) : null,
        categoryId: data.categoryId != null && String(data.categoryId) !== '' ? Number(data.categoryId) : null,
        isActive: data.isActive,
        sortOrder: data.sortOrder != null ? Number(data.sortOrder) : undefined,
      });
    },
  },
  fields: [
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      placeholder: 'Select type',
      options: [
        { value: '', label: 'Select...' },
        { value: 'product', label: 'Product' },
        { value: 'service', label: 'Service' },
      ],
      validation: { required: true },
    },
    { name: 'name', label: 'Name', type: 'text', placeholder: 'Name', validation: { required: true } },
    { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Description', rows: 3 },
    { name: 'defaultPrice', label: 'Default price', type: 'number', placeholder: '0.00', min: 0, step: 0.01, helperText: '€' },
    { name: 'manufacturerId', label: 'Manufacturer', type: 'select', placeholder: 'Select manufacturer', options: [{ value: '', label: 'None' }], apiEndpoint: '/manufacturers', labelField: 'name', valueField: 'id' as const },
    { name: 'categoryId', label: 'Category', type: 'select', placeholder: 'Select category', options: [{ value: '', label: 'None' }], apiEndpoint: '/catalog-categories', labelField: 'name', valueField: 'id' as const },
    { name: 'sortOrder', label: 'Sort order', type: 'number', placeholder: '0', min: 0, helperText: 'Lower numbers appear first in lists' },
    { name: 'isActive', label: 'Active', type: 'checkbox' },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Catalog item',
        icon: '📦',
        gridCols: 'md:grid-cols-12',
        fields: ['type', 'name', 'description', 'defaultPrice', 'manufacturerId', 'categoryId', 'sortOrder', 'isActive'],
        fieldSpans: { type: 'md:col-span-4', name: 'md:col-span-8', description: 'md:col-span-12', defaultPrice: 'md:col-span-4', manufacturerId: 'md:col-span-4', categoryId: 'md:col-span-4', sortOrder: 'md:col-span-4', isActive: 'md:col-span-4' },
        collapsible: false,
      },
    ],
  } as FormLayout,
  renderEditFormExtra: ({ isEdit, data, id }: { isEdit: boolean; data?: CatalogItem; id?: number }) => {
    if (!isEdit || !data || id == null) {
      return (
        <>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Product photos</h3>
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 px-4 py-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">Product photos can be added after you save the item.</p>
          </div>
        </>
      );
    }
    return React.createElement(ProductPhotosFormBlock, { itemId: id, item: data });
  },
};

/** Fetches catalog item image with auth and displays as thumbnail (img src does not send JWT). */
function CatalogItemThumbnail({
  catalogItemId,
  fileId,
  className,
}: {
  catalogItemId: number;
  fileId: number;
  className?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let blobUrl: string | null = null;
    api
      .get(`/catalog-items/${catalogItemId}/files/${fileId}`, { responseType: 'blob' })
      .then((res) => {
        blobUrl = URL.createObjectURL(res.data as Blob);
        setSrc(blobUrl);
      })
      .catch(() => setError(true));
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
    };
  }, [catalogItemId, fileId]);
  if (error || !src) {
    return (
      <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-xs" aria-hidden>
        —
      </div>
    );
  }
  return <img src={src} alt="" className={className} />;
}

export const getColumns = (
  formatDate: (dateString?: string) => string,
  formatCurrency?: (amount?: number | null) => string,
  colors?: ThemeColors
): Column<CatalogItem>[] => {
  const fmt = formatCurrency || moduleConfig.utils.formatCurrency;
  const text = colors?.text || 'text-gray-900';
  const formatType = moduleConfig.utils.formatType;
  const getThumbnailFile = (row: CatalogItem) => {
    const files = row.FileAttachment ?? [];
    if (files.length === 0) return null;
    const primaryId = row.primaryFileAttachmentId;
    const primary = primaryId ? files.find((f) => f.id === primaryId) : null;
    return primary ?? files[0];
  };
  return [
    {
      key: 'thumbnail',
      header: '',
      sortable: false,
      render: (_, row) => {
        const file = getThumbnailFile(row);
        if (!file || !file.fileType?.toLowerCase().startsWith('image/')) {
          return <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-xs">—</div>;
        }
        return (
          <CatalogItemThumbnail
            catalogItemId={row.id}
            fileId={file.id}
            className="w-10 h-10 rounded object-contain bg-gray-100"
          />
        );
      },
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (_, row) => (
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className={`text-sm font-medium truncate ${text}`}>{row.name}</span>
          <Badge variant={row.type === 'service' ? 'info' : 'warning'} className="shrink-0">
            {formatType(row.type)}
          </Badge>
        </span>
      ),
    },
    { key: 'manufacturer', header: 'Manufacturer', sortable: false, render: (_, row) => <span className={`text-sm ${text}`}>{row.manufacturer?.name ?? '-'}</span> },
    { key: 'category', header: 'Category', sortable: false, render: (_, row) => <span className={`text-sm ${text}`}>{row.category?.name ?? '-'}</span> },
    { key: 'defaultPrice', header: 'Default price', sortable: true, render: (_, row) => <span className={`text-sm ${text} tabular-nums`}>{fmt(row.defaultPrice)}</span> },
    { key: 'isActive', header: 'Active', sortable: true, render: (_, row) => (
      <Badge variant={statusVariantFromBoolean(!!row.isActive)}>
        {row.isActive ? 'Yes' : 'No'}
      </Badge>
    ) },
  ];
};

export const getDetailSections = (
  item: CatalogItem,
  formatDate: (dateString?: string | null) => string,
  formatCurrency: (amount?: number | null) => string
) => {
  const sections: Array<{
    title: string;
    icon?: string;
    fields?: Array<{ label: string; value: React.ReactNode }>;
    content?: React.ReactNode;
  }> = [
    {
      title: 'Information',
      icon: '📦',
      fields: [
        { label: 'Name', value: item.name },
        { label: 'Type', value: moduleConfig.utils.formatType(item.type) },
        { label: 'Description', value: item.description || '-' },
        { label: 'Default price', value: formatCurrency(item.defaultPrice) },
        { label: 'Manufacturer', value: item.manufacturer?.name || '-' },
        { label: 'Category', value: item.category?.name || '-' },
        { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
        { label: 'Created', value: formatDate(item.createdAt ?? undefined) },
        { label: 'Updated', value: formatDate(item.updatedAt ?? undefined) },
      ],
    },
  ];
  // Product photos: upload + gallery (use content so the block is clearly visible)
  sections.push({
    title: 'Product photos',
    icon: '🖼️',
    content: (
      <div className="space-y-4">
        <CatalogItemUploadSection itemId={item.id} />
        <EntityImageGallery
          files={item.FileAttachment || []}
          getImageUrl={(fileId: number) => `${API_BASE_URL}/catalog-items/${item.id}/files/${fileId}`}
          fetchImage={async (fileId: number) => {
            const res = await api.get(`/catalog-items/${item.id}/files/${fileId}`, { responseType: 'blob' });
            return URL.createObjectURL(res.data as Blob);
          }}
        />
      </div>
    ),
  });
  const allFiles = item.FileAttachment || [];
  if (allFiles.length > 0) {
    sections.push({
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/catalog-items/${item.id}/files`,
            files: allFiles,
            defaultDownloadName: `catalog_item_${item.id}_files.zip`,
            getViewFileUrl: (fileId: number) => `/catalog-items/${item.id}/files/${fileId}`,
          }),
        },
      ],
    });
  }
  return sections;
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add')
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  return null;
};

export type CatalogItemFormData = Partial<CatalogItem> & { type?: string; name?: string };

export const getInitialFormData = (): CatalogItemFormData => ({
  type: 'product',
  name: '',
  description: '',
  defaultPrice: undefined,
  manufacturerId: undefined,
  categoryId: undefined,
  isActive: true,
  sortOrder: undefined,
});

export const mapItemToFormData = (item: CatalogItem): CatalogItemFormData => ({
  type: item.type,
  name: item.name,
  description: item.description ?? '',
  defaultPrice: item.defaultPrice ?? undefined,
  manufacturerId: item.manufacturerId != null ? item.manufacturerId : undefined,
  categoryId: item.categoryId != null ? item.categoryId : undefined,
  isActive: item.isActive,
  sortOrder: item.sortOrder ?? undefined,
});

function DuplicateCatalogItemButton({
  itemId,
  onSuccess,
  onError,
}: {
  itemId: number;
  onSuccess?: (createdId: number) => void;
  onError?: () => void;
}) {
  const [duplicating, setDuplicating] = useState(false);
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (duplicating) return;
    setDuplicating(true);
    try {
      const { duplicateCatalogItem } = await import('../../services/mutations');
      const created = await duplicateCatalogItem(itemId);
      if (created?.id) onSuccess?.(created.id);
    } catch {
      onError?.();
    } finally {
      setDuplicating(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={duplicating}
      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
      title="Duplicate"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
    </button>
  );
}

export const getTableActions = (
  item: CatalogItem,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

